import { usePulse, useDeliverables } from "@/hooks/useLive";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

export function PulsePanel() {
  const WEEKLY_PULSE = usePulse();
  const DELIVERABLES = useDeliverables();

  const live = {
    onTrack: DELIVERABLES.filter((d) => d.status === "in_progress" || d.status === "complete").length,
    atRisk: DELIVERABLES.filter((d) => d.status === "at_risk").length,
    blocked: DELIVERABLES.filter((d) => d.status === "blocked").length,
    complete: DELIVERABLES.filter((d) => d.status === "complete").length,
    total: DELIVERABLES.length,
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Live status · now</CardTitle>
          <p className="text-caption text-muted-foreground">
            Auto-calculated from all {live.total} deliverables in real time.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "On track / active", value: live.onTrack, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "At risk", value: live.atRisk, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Blocked", value: live.blocked, color: "text-destructive", bg: "bg-destructive/5" },
              { label: "Complete", value: live.complete, color: "text-sky-600", bg: "bg-sky-50" },
            ].map((s) => (
              <div key={s.label} className={cn("rounded-lg p-4 text-center", s.bg)}>
                <div className={cn("text-h2 font-semibold tabular", s.color)}>{s.value}</div>
                <div className="mt-1 text-micro uppercase tracking-wider text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Weekly pulse · history</CardTitle>
          <p className="text-caption text-muted-foreground">
            Weekly snapshot for leadership. Feeds the Google Chat Monday update.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {WEEKLY_PULSE.length === 0 ? (
            <div className="py-10 text-center text-caption text-muted-foreground">No pulse history yet.</div>
          ) : (
            <table className="w-full text-caption">
              <thead>
                <tr className="bg-fello-navy text-white">
                  {["Week", "On track", "At risk", "Blocked", "Highlight", "Decision needed"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-caption font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {WEEKLY_PULSE.map((w, i) => (
                  <tr key={w.week} className={i % 2 === 0 ? "bg-muted/30" : "bg-card"}>
                    <td className="whitespace-nowrap px-3 py-3 font-semibold text-foreground tabular">{w.week}</td>
                    <td className={cn("px-3 py-3 tabular font-semibold", w.onTrack > 0 ? "text-emerald-600" : "text-muted-foreground")}>{w.onTrack || "—"}</td>
                    <td className={cn("px-3 py-3 tabular font-semibold", w.atRisk > 0 ? "text-amber-600" : "text-muted-foreground")}>{w.atRisk || "—"}</td>
                    <td className={cn("px-3 py-3 tabular font-semibold", w.blocked > 0 ? "text-destructive" : "text-muted-foreground")}>{w.blocked || "—"}</td>
                    <td className={cn("px-3 py-3", w.highlight ? "text-foreground" : "text-muted-foreground")}>{w.highlight || "—"}</td>
                    <td className={cn("px-3 py-3", w.decision ? "font-semibold text-destructive" : "text-muted-foreground")}>{w.decision || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
